import {classNames} from "@reactivated";
import {type MotionValue, motion, motionValue, useTransform} from "motion/react";
import React from "react";

// SimpleScene's floating chrome: the bottom bar and the header bubbles. Each
// reads how far it is from content: 1 at rest (nothing under it), 0 while
// content runs under it. The bar reads the end of the scroll, the header its
// start. The glass never changes; only the shadow lifts.
const BarSurfaceContext = React.createContext<MotionValue<number> | null>(null);
const HeaderSurfaceContext = React.createContext<MotionValue<number> | null>(null);

export const BarSurfaceProvider = BarSurfaceContext.Provider;
export const HeaderSurfaceProvider = HeaderSurfaceContext.Provider;

const AT_REST = motionValue(1);

export const useBarSurface = () => React.useContext(BarSurfaceContext) ?? AT_REST;
export const useHeaderSurface = () => React.useContext(HeaderSurfaceContext) ?? AT_REST;

function ChromeSurface(props: {
    rest: MotionValue<number>;
    tone: "glass" | "primary";
    className?: string;
    children: React.ReactNode;
}) {
    const lift = useTransform(props.rest, [0, 1], [1, 0]);
    return (
        <div
            className={classNames(
                "relative shadow-chrome-rest backdrop-blur-[24px] backdrop-saturate-[1.4]",
                props.tone === "primary" ? "bg-chrome-primary" : "bg-chrome-glass",
                "dark:bg-slate-800 dark:shadow-none dark:backdrop-blur-none",
                props.className,
            )}
        >
            <motion.span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-chrome-over dark:hidden"
                style={{opacity: lift}}
            />
            <div className="relative overflow-hidden rounded-[inherit]">
                {props.children}
            </div>
        </div>
    );
}

// A floating surface in the bar: glass, or the one brighter primary action.
export function BarSurface(props: {
    tone?: "glass" | "primary";
    className?: string;
    children: React.ReactNode;
}) {
    const rest = useBarSurface();
    return (
        <ChromeSurface
            rest={rest}
            tone={props.tone ?? "glass"}
            className={props.className}
        >
            {props.children}
        </ChromeSurface>
    );
}

// A bubble in the header: the menu, back, the date and the streak.
export function HeaderSurface(props: {className?: string; children: React.ReactNode}) {
    const rest = useHeaderSurface();
    return (
        <ChromeSurface rest={rest} tone="glass" className={props.className}>
            {props.children}
        </ChromeSurface>
    );
}
